import React, { createContext, useContext, useState } from 'react';
import { useToast } from '../hooks/use-toast';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState([]);
  const { toast } = useToast();

  const addToCart = (item) => {
    setCartItems(prevItems => {
      const existingItem = prevItems.find(cartItem => 
        cartItem.id === item.id && cartItem.type === item.type && cartItem.format === item.format
      );
      
      if (existingItem) {
        toast({
          title: "Item already in cart",
          description: `${item.title} is already in your cart.`,
        });
        return prevItems;
      }

      toast({
        title: "Added to cart!",
        description: `${item.title} has been added to your cart.`,
      });

      return [...prevItems, { ...item, quantity: 1 }];
    });
  };

  const removeFromCart = (itemId, type, format) => {
    setCartItems(prevItems => 
      prevItems.filter(item => 
        !(item.id === itemId && item.type === type && item.format === format)
      )
    );
    
    toast({
      title: "Removed from cart",
      description: "Item has been removed from your cart.",
    });
  };

  const updateQuantity = (itemId, type, format, quantity) => {
    if (quantity <= 0) {
      removeFromCart(itemId, type, format);
      return;
    }

    setCartItems(prevItems =>
      prevItems.map(item =>
        item.id === itemId && item.type === type && item.format === format
          ? { ...item, quantity }
          : item
      )
    );
  };

  const clearCart = () => {
    setCartItems([]);
  };

  const getTotalPrice = () => {
    return cartItems.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  const getTotalItems = () => {
    return cartItems.reduce((total, item) => total + item.quantity, 0);
  };

  const value = {
    cartItems,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    getTotalPrice,
    getTotalItems,
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};